import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/app/api";

const input = "w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/15";
const label = "text-xs font-bold uppercase text-slatey";
const EVENTS = [["application_submitted", "Application submitted"], ["application_accepted", "Application accepted"], ["learner_enrolled", "Learner enrolled"], ["learner_started", "Learner started"], ["commission_received", "Commission received"]];

export default function AffiliateProgramme() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/affiliate-programme").then((r) => setForm(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const { id, version, history, terms_history, updated_at, ...payload } = form;
      await api.put("/admin/affiliate-programme", payload);
      toast.success("Programme settings saved — a new configuration version was created.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!form) return <p className="text-sm text-slatey">Loading…</p>;
  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const scopeText = (form.scope?.opportunity_ids || []).join(", ");

  return (
    <div data-testid="affiliate-programme" className="mx-auto max-w-3xl space-y-6">
      <Link to="/app/admin/affiliates" className="inline-flex items-center gap-2 text-sm font-bold text-slatey hover:text-cobalt"><ArrowLeft className="h-4 w-4" /> Affiliates</Link>
      <div>
        <h1 className="font-display text-3xl font-bold text-ink">Affiliate programme settings</h1>
        <p className="mt-1 text-sm text-slatey">Configuration version {form.version} — every save versions the configuration; historical rewards keep the rules they were created under.</p>
      </div>

      <section className="space-y-4 rounded-3xl border border-slate-200 bg-white p-6">
        <div>
          <p className={label}>Programme name</p>
          <input value={form.programme_name || ""} onChange={(e) => update("programme_name", e.target.value)} className={`${input} mt-1`} data-testid="programme-name" />
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold text-ink">
          <input type="checkbox" checked={Boolean(form.active)} onChange={(e) => update("active", e.target.checked)} className="h-4 w-4 accent-cobalt" data-testid="programme-active" /> Programme active
        </label>
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <p className={label}>Reward type</p>
            <select value={form.reward_type} onChange={(e) => update("reward_type", e.target.value)} className={`${input} mt-1`} data-testid="programme-reward-type">
              <option value="fixed_amount">Fixed amount</option>
              <option value="percentage">Percentage of tuition</option>
            </select>
          </div>
          <div>
            <p className={label}>Reward value</p>
            <input type="number" step="0.01" value={form.reward_amount ?? ""} onChange={(e) => update("reward_amount", e.target.value ? Number(e.target.value) : null)} className={`${input} mt-1`} data-testid="programme-reward-amount" />
          </div>
          <div>
            <p className={label}>Currency</p>
            <input value={form.currency || "GBP"} onChange={(e) => update("currency", e.target.value.toUpperCase())} className={`${input} mt-1`} data-testid="programme-currency" />
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <p className={label}>Qualifying event</p>
            <select value={form.qualifying_event} onChange={(e) => update("qualifying_event", e.target.value)} className={`${input} mt-1`} data-testid="programme-qualifying-event">
              {EVENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <p className={label}>Attribution window (days)</p>
            <input type="number" value={form.attribution_window_days ?? ""} onChange={(e) => update("attribution_window_days", e.target.value ? Number(e.target.value) : null)} className={`${input} mt-1`} data-testid="programme-window" />
          </div>
          <div>
            <p className={label}>Minimum payout threshold</p>
            <input type="number" step="0.01" value={form.minimum_payout_threshold ?? ""} onChange={(e) => update("minimum_payout_threshold", e.target.value ? Number(e.target.value) : null)} className={`${input} mt-1`} data-testid="programme-threshold" />
          </div>
        </div>
        <div>
          <p className={label}>Eligibility conditions</p>
          <textarea value={form.eligibility_conditions || ""} onChange={(e) => update("eligibility_conditions", e.target.value)} rows={2} className={`${input} mt-1`} data-testid="programme-eligibility" />
        </div>
        <div>
          <p className={label}>Scope — opportunity IDs (comma-separated, blank = all)</p>
          <input value={scopeText} onChange={(e) => update("scope", { ...(form.scope || {}), opportunity_ids: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className={`${input} mt-1`} data-testid="programme-scope" />
        </div>
      </section>

      <section className="space-y-4 rounded-3xl border border-slate-200 bg-white p-6">
        <h2 className="font-display text-xl font-bold text-ink">Affiliate Terms &amp; Conditions</h2>
        <p className="text-xs text-slatey">Changing terms creates a new terms version. Affiliates keep the version they accepted; they are asked to re-accept on their dashboard.</p>
        <div>
          <p className={label}>Terms version</p>
          <input value={form.terms_version || ""} onChange={(e) => update("terms_version", e.target.value)} className={`${input} mt-1`} data-testid="programme-terms-version" />
        </div>
        <div>
          <p className={label}>Terms text</p>
          <textarea value={form.terms_text || ""} onChange={(e) => update("terms_text", e.target.value)} rows={5} className={`${input} mt-1`} data-testid="programme-terms-text" />
        </div>
        <div data-testid="terms-history">
          <p className={label}>Terms version history</p>
          <ul className="mt-1 space-y-1 text-xs text-slatey">
            {(form.terms_history || []).slice().reverse().map((entry, index) => (
              <li key={index}>v{entry.version} · {entry.effective_from ? entry.effective_from.slice(0, 10) : "initial"} · by {entry.by}</li>
            ))}
          </ul>
        </div>
      </section>

      <button onClick={save} disabled={busy} data-testid="programme-save"
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-5 py-3.5 text-sm font-bold text-white disabled:opacity-50">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save settings (new version)
      </button>

      <section className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="programme-history">
        <h2 className="font-display text-xl font-bold text-ink">Configuration history</h2>
        <ul className="mt-3 space-y-2">
          {(form.history || []).slice().reverse().map((entry, index) => (
            <li key={index} className="rounded-2xl bg-surface px-4 py-3 text-xs text-slatey">
              <span className="font-bold text-ink">v{entry.version}</span> · {entry.note} · {entry.by} · {entry.at?.slice(0, 16).replace("T", " ")}
            </li>
          ))}
          {!(form.history || []).length && <li className="text-sm text-slatey">No changes yet — version {form.version} is the initial configuration.</li>}
        </ul>
      </section>
    </div>
  );
}
